<template>
  <div v-if="recurringInvoice" class="row">
    <div class="col-12 col-xl-3">
      <document-settings :title="$t('message.recurringInvoiceDetails')">
        <div slot="body">
          <div style="columns: 150px 3">
            <fg-input :label="$t('message.invoiceRecipient')">
              <div class="d-inline-block w-100">
                <router-link
                  tag="a"
                  :to="`/customers/view/${recurringInvoice.to._id}`"
                  >{{ recurringInvoice.to.companyName }}</router-link
                >
              </div>
            </fg-input>
            <fg-input :label="$t('message.recurringInvoiceFirstIssue')">
              <div class="d-inline-block w-100">
                {{ recurringInvoice.sentDate | dateToHuman }}
              </div>
            </fg-input>
            <fg-input :label="$t('message.recurringInvoiceTimeToPay')">
              <div class="d-inline-block w-100">
                {{ recurringInvoice.paymentDays }}
              </div>
            </fg-input>
            <fg-input :label="$t('message.recurringInvoiceRepeatEvery')">
              <div class="d-inline-block w-100">{{ duration }}</div>
            </fg-input>
            <fg-input :label="$t('message.recurringInvoiceEnding')">
              <div class="d-inline-block w-100">{{ ending }}</div>
            </fg-input>
          </div>
          <hr />
          <div style="columns: 150px 3">
            <fg-input :label="$t('message.status')">
              <div>
                <span :class="statusClass">{{ status }}</span>
              </div>
            </fg-input>
            <fg-input :label="$t('message.invoicesIssued')">
              <div class="d-inline-block w-100">
                {{ recurringInvoice.issuedCount }}
              </div>
            </fg-input>
            <fg-input :label="$t('message.nextInvoiceDate')">
              <div class="d-inline-block w-100">
                {{ recurringInvoice.nextInvoiceDate | dateToHuman }}
              </div>
            </fg-input>
          </div>
          <hr />
          <n-button
            v-if="recurringInvoice.status === 'ACTIVE'"
            type="danger"
            round
            @click.native="onTerminate"
            >{{ $t("message.cancelRecurringInvoice") }}</n-button
          >
          <router-link to="/invoices">
            <n-button type="secondary" class="ml-1" round simple>
              {{ $t("message.goBack") }}
            </n-button>
          </router-link>
        </div>
      </document-settings>
    </div>
    <div class="col-12 col-xl-9">
      <document
        :title="$t('message.invoice')"
        :header-items="headerItems"
        :from-label="$t('message.invoiceFrom')"
        :from="recurringInvoice.from"
        :to-label="$t('message.invoiceRecipient')"
        :to="recurringInvoice.to"
        :sub-total="recurringInvoice.subTotal.displayValue"
        :vat-total="recurringInvoice.vatTotal.displayValue"
        :total="recurringInvoice.total.displayValue"
      >
        <template slot="table">
          <b-table
            :fields="tableFields"
            :items="recurringInvoice.items"
            stacked="md"
          ></b-table>
        </template>
      </document>
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import { dateToHuman, formatMoney } from "@/helpers/helpers.js";
import { Document, DocumentSettings } from "@/components";

export default {
  components: {
    Document,
    DocumentSettings
  },
  data() {
    return {
      tableFields: [
        {
          key: "description",
          label: this.$t("message.invoiceItem")
        },
        {
          key: "description",
          label: this.$t("message.invoiceItem"),
          class: "document__table__description"
        },
        {
          key: "price",
          label: this.$t("message.invoiceItemUnitCost"),
          class: "document__table__price"
        },
        {
          key: "quantity",
          label: this.$t("message.invoiceItemQuantity"),
          class: "document__table__quantity"
        },
        {
          key: "vat",
          label: this.$t("message.invoiceItemVat"),
          class: "document__table__vat"
        },
        {
          key: "total",
          label: this.$t("message.invoiceItemPrice"),
          class: "document__table__total",
          formatter: (value, key, item) =>
            formatMoney(item.price * item.quantity)
        }
      ]
    };
  },
  methods: {
    ...mapActions(["fetchRecurringInvoice", "terminateRecurringInvoice"]),
    async onTerminate() {
      const confirmationResult = await this.$swal({
        title: this.$t("message.areYouSure"),
        text: this.$t("message.cancelRecurringInvoiceMessage"),
        type: "warning",
        showCancelButton: true,
        confirmButtonText: "Confirm",
        cancelButtonText: "Cancel",
        confirmButtonClass: "btn btn-success btn-fill",
        cancelButtonClass: "btn btn-danger btn-fill",
        buttonsStyling: false
      });

      if (!confirmationResult.value) return;

      this.loader = this.$loading.show();

      try {
        await this.terminateRecurringInvoice(this.recurringInvoice);
        this.$notify({
          type: "success",
          title: this.$t("message.success"),
          text: this.$t("message.cancelRecurringInvoiceSuccess")
        });
        this.$router.push("/invoices");
      } catch (error) {
        this.$notify({
          type: "danger",
          title: this.$t("message.error"),
          text:
            this.$t("message.cancelRecurringInvoiceFailure") +
            `${error.message}.`
        });
      } finally {
        this.loader.hide();
      }
    }
  },
  computed: {
    ...mapGetters(["recurringInvoice"]),
    headerItems() {
      return [
        {
          label: this.$t("message.invoiceNumber"),
          value: "TBC"
        },
        {
          label: this.$t("message.dateSent"),
          value: "TBC"
        },
        {
          label: this.$t("message.dateDue"),
          value: "TBC"
        }
      ];
    },
    duration() {
      if (!this.recurringInvoice) return;

      const { recurringConfig } = this.recurringInvoice;

      if (recurringConfig.daily) return "Day";
      if (recurringConfig.weekly) return "Week";
      if (recurringConfig.monthly) return "Month";
      if (recurringConfig.yearly) return "Year";
    },
    ending() {
      if (!this.recurringInvoice.status === "ACTIVE") {
        return "Ended";
      }

      const { endDate } = this.recurringInvoice;

      if (endDate) {
        return `On ${dateToHuman(endDate.seconds)}`;
      }

      const { totalOccurrences, issuedCount } = this.recurringInvoice;

      if (totalOccurrences) {
        return `${this.$t("message.recurringInvoiceAfter")} ${totalOccurrences -
          (issuedCount ? issuedCount : 0)} ${_.toLower(
          this.$t("message.recurringInvoiceMore")
        )} ${_.toLower(this.$t("message.recurringInvoiceOccurrences"))}`;
      }
    },
    statusClass() {
      const { status } = this.recurringInvoice;
      if (status === "COMPLETE") return "badge badge-success";
      if (status === "CANCELLED") return "badge badge-warning";
      return "badge badge-info";
    },
    status() {
      const { status } = this.recurringInvoice;
      if (status === "COMPLETE")
        return this.$t("message.recurringInvoiceStatusComplete");
      if (status === "CANCELLED")
        return this.$t("message.recurringInvoiceStatusCancelled");
      return this.$t("message.recurringInvoiceStatusActive");
    }
  },
  filters: {
    dateToHuman
  },
  async created() {
    try {
      await this.fetchRecurringInvoice(this.$route.params.id);
    } catch (error) {
      this.router.push("/invoices");
      this.$notify({
        type: "danger",
        title: this.$t("message.error"),
        text: this.$t("message.viewRecurringInvoiceFailure")
      });
    }
  }
};
</script>
<style lang="scss" scoped></style>
